'use client';

import { useRouter } from 'next/navigation';
import { ReviewMushroomUnit, ReviewAssetHome, ReviewBuildingPrompt, IconEdit } from '@repo/svgs';
import { ResponsiveModal } from '@/components/ui/ResponsiveModal';
import { Button } from '@/components/ui';
import { useAnalytics } from '@/hooks/useAnalytics';
import { usePromptContext } from '@/providers/PromptProvider';
import type { TriggerPageType } from '@/lib/prompts/storage';

interface ReviewPromptProps {
  open: boolean;
}

/**
 * Per-surface content variants. Same modal shell across all four,
 * only the hero image + copy differ. When building / pSEO Figmas
 * land, add their entries here.
 */
type ReviewVariant = {
  Image: React.ComponentType<{ className?: string }>;
  headline: string;
  body: string;
};

/**
 * Shared by the building and owner surfaces — the owner page makes the
 * same ask, so both render identical hero + copy. Kept as one object so
 * the two can't drift apart.
 */
const BUILDING_REVIEW_VARIANT: ReviewVariant = {
  Image: ReviewBuildingPrompt,
  headline: 'Leave a review to read reviews!',
  body:
    'Take 1 min to ANONYMOUSLY review your building and help the next' +
    ' renter who considers your spot!',
};

const REVIEW_VARIANTS: Record<TriggerPageType, ReviewVariant> = {
  unit: {
    Image: ReviewMushroomUnit,
    headline: '“Mushrooms grew from the floor!”',
    body:
      "We've helped renters avoid mold, pests, and bad landlords. But we" +
      " can't do it without your help! Take 1 min to ANONYMOUSLY review" +
      ' your building.',
  },
  home: {
    Image: ReviewAssetHome,
    headline: 'A favor to ask!',
    body:
      'Take 1 min to ANONYMOUSLY review your building. Our mission is to' +
      " bring transparency to renting, but we can't do it without your" +
      ' help.',
  },
  building: BUILDING_REVIEW_VARIANT,
  owner: BUILDING_REVIEW_VARIANT,
  pseo: {
    // Same hero as home per Figma.
    Image: ReviewAssetHome,
    headline: 'Support our mission!',
    body:
      'Take 1 min to ANONYMOUSLY review your building. We can’t hold' +
      ' landlords accountable without your help!',
  },
};

/**
 * Review prompt. Fires per-surface (unit_view #3, home_view #2, etc.)
 * per PROMPT_THRESHOLDS.review. Same shell as ReportPrompt — AppBar
 * X, no divider, 448px on desktop, BottomSheet on mobile. Only the
 * hero image + copy vary by surface (see REVIEW_VARIANTS).
 */
export function ReviewPrompt({ open }: ReviewPromptProps) {
  const router = useRouter();
  const analytics = useAnalytics();
  const { activeReviewScreen, dismissActivePrompt, markPromptShown } = usePromptContext();

  // Fall back to 'unit' as the safest default if the surface wasn't
  // tracked (shouldn't happen in practice — schedulePrompt always
  // sets it alongside activePrompt='review').
  const screen: TriggerPageType = activeReviewScreen ?? 'unit';
  const variant = REVIEW_VARIANTS[screen];
  const { Image, headline, body } = variant;

  const handleReview = () => {
    // The surface drives both the analytics `screen` param and the
    // per-surface shown-flag that markPromptShown flips.
    analytics.track('review_prompt_review_your_building', { screen });
    markPromptShown('review', screen);

    // Close before navigating so the overlay doesn't linger across
    // the route transition (matches ReportPrompt behavior).
    dismissActivePrompt();

    // Every surface lands on the review search page so the user picks
    // the building they actually live in — the building in scope when
    // the prompt fired is rarely theirs.
    router.push('/review');
  };

  return (
    <ResponsiveModal
      open={open}
      onClose={dismissActivePrompt}
      label=""
      maxWidth="md"
      closeOnOverlayClick
      contentClassName="rounded-3xl"
      desktopBodyClassName="rounded-3xl"
      // Strip the AppBar's built-in grey divider under the X on both
      // desktop and mobile — this modal is header-less by design.
      crossPlatformClassName="[&_[role=banner]]:!border-b-0"
    >
      {/* `-mt-4` negates ResponsiveModal's top py-4 so the image sits
          flush against the AppBar. `md:pb-2` tops up the wrapper's 16px
          bottom padding to the 24px desktop spec (mobile keeps 16).
          Gap 4 between items. */}
      <div className="flex flex-col gap-4 -mt-4 md:pb-2">
        {/* Image spans full content width — left edge aligns with the
            title (both sit at ResponsiveModal's px-6 inset). */}
        <Image className="w-full h-auto" />
        <div className="flex flex-col gap-2">
          <h6 className="text-heading-6 font-semibold text-primary text-left">{headline}</h6>
          <p className="text-body-2 text-primary text-left">{body}</p>
        </div>
        <Button variant="primary" size="md" fullWidth className="gap-2" onClick={handleReview}>
          <IconEdit className="w-4 h-4 icon-white" />
          Review your building
        </Button>
      </div>
    </ResponsiveModal>
  );
}
